import { MutationCache, QueryClient, useMutation, useQuery } from '@tanstack/react-query';
import type {
  DefaultError,
  QueryKey,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

/**
 * The one QueryClient for server state (ui-refactor-server-state).
 *
 * Defaults:
 * - `staleTime` 60s: a screen re-opened within a minute draws what it read rather than asking again.
 * - `retry: false`: the transport already retries a read that failed for a transient reason (`apiClient`'s
 *   interceptors, ui-refactor-api-client). Retrying here as well would multiply the attempts and delay every
 *   genuine error. Mutations are never retried; a write is not idempotent.
 * - No refetch on window focus: the screens here are forms and tables a user is working in, and a silent re-read
 *   under them would move rows.
 */
export function createAppQueryClient(): QueryClient {
  const client: QueryClient = new QueryClient({
    defaultOptions: {
      queries: { staleTime: 60_000, retry: false, refetchOnWindowFocus: false },
      mutations: { retry: false },
    },
    /*
      Every successful write re-checks the bell.

      The badge is polled every thirty seconds, which is the right cadence for something arriving from
      elsewhere -- another user's approval, a background import. It is the wrong cadence for the user's OWN
      action: publish a BID and the notification exists immediately, but the badge would sit unchanged for
      up to half a minute, which reads as the feature not working.

      A write is the only thing that can produce a notification, so this is the complete set of moments the
      count can change from this tab. It costs one small GET per write, and only when something is actually
      listening -- an invalidation with no mounted observer refetches nothing.
    */
    mutationCache: new MutationCache({
      onSuccess: () => {
        void client.invalidateQueries({ queryKey: ['notifications'] });
      },
    }),
  });
  return client;
}

/**
 * The application's client, a module singleton.
 *
 * `QueryProvider` provides it, and `useServerQuery` / `useServerMutation` name it explicitly as well. A component
 * therefore works under any test harness without wrapping each render in a provider, and the cache is the same one
 * either way. The test setup clears it after every test. `QueryProvider` clears it when the signed-in user changes
 * and resets it when the selected entity changes: every response is scoped to that entity (`X-Entity-Id`), so a
 * cached answer would be the previous entity's.
 */
export const appQueryClient = createAppQueryClient();

/** `useQuery` against the application's client. Hooks use this, never `useQuery` directly. */
export function useServerQuery<TQueryFnData, TError = DefaultError, TData = TQueryFnData, TKey extends QueryKey = QueryKey>(
  options: UseQueryOptions<TQueryFnData, TError, TData, TKey>,
): UseQueryResult<TData, TError> {
  return useQuery(options, appQueryClient);
}

/** `useMutation` against the application's client. */
export function useServerMutation<TData = unknown, TError = DefaultError, TVariables = void, TContext = unknown>(
  options: UseMutationOptions<TData, TError, TVariables, TContext>,
): UseMutationResult<TData, TError, TVariables, TContext> {
  return useMutation(options, appQueryClient);
}
